
import aliasb03645cf6abd4d56a73b13687765a5d8 from '@/locale/i18nUtils.js';
/**
 * @Description: 监听 #app 滚动，自动关闭 iView / View UI 的 transfer 浮层
 * @author jing
 * @date 2026/4/14
 *
 * 适用场景：
 * 1. #app 设置了 overflow-y: auto / scroll
 * 2. Select / Dropdown / Cascader / DatePicker 等组件开启了 transfer
 * 3. 页面滚动时，浮层出现错位、悬空
 *
 * 方案说明：
 * 1. 不直接操作浮层 DOM 的 display / visibility / opacity
 * 2. 只通过 blur + 模拟点击空白区域，让组件自己关闭
 * 3. 避免出现“下拉框永久被隐藏”的问题
 */

let appScrollEl = null;
let appScrollHandler = null;
let appScrollRaf = null;

const defaultIgnoreScrollSelectors = [
  '.ivu-select-dropdown',
  '.ivu-select-dropdown-list',
  '.ivu-dropdown-menu',
  '.ivu-cascader-menu',
  '.ivu-picker-panel-content',
  '.ivu-time-picker-cells',
  '.ivu-time-picker-cells-list'
];

/**
 * 获取滚动容器
 * @param {String} selector
 * @returns {Element|null}
 */
function getAppScrollContainer(selector = '#app') {
  return document.querySelector(selector);
}

function matchesSelector(el, selector) {
  if (typeof el.matches === 'function') {
    return el.matches(selector);
  }

  const doc = el.document || el.ownerDocument;
  const nodes = doc ? doc.querySelectorAll(selector) : [];

  for (let i = 0; i < nodes.length; i++) {
    if (nodes[i] === el) {
      return true;
    }
  }

  return false;
}

function shouldIgnoreScroll(event, ignoreSelectors = defaultIgnoreScrollSelectors) {
  let current = event && event.target;

  while (current && current !== document && current !== window) {
    if (current.nodeType === 1 && ignoreSelectors.some(selector => matchesSelector(current, selector))) {
      return true;
    }
    current = current.parentNode;
  }

  return false;
}

/**
 * 当前是否存在打开的 iView 浮层
 * 这里只做判断，不做样式修改
 * @returns {Boolean}
 */
function hasOpenedPoppers() {
  const selectors = [
    '.ivu-select-visible',
    '.ivu-dropdown-visible',
    '.ivu-cascader-menu',
    '.ivu-date-picker-focused',
    '.ivu-time-picker-cells-focused'
  ];
  
  return selectors.some(selector => document.querySelector(selector));
}

/**
 * 让当前激活元素失焦
 */
function blurActiveElement() {
  const activeEl = document.activeElement;
  if (activeEl && typeof activeEl.blur === 'function') {
    activeEl.blur();
  }
}

/**
 * 模拟点击空白区域，让 iView 自己关闭浮层
 */
function triggerBodyMousedown() {
  const event = new MouseEvent('mousedown', {
    bubbles: true,
    cancelable: true,
    view: window
  });
  
  document.body.dispatchEvent(event);
}

/**
 * 模拟 click，补充关闭部分组件
 */
function triggerBodyClick() {
  const event = new MouseEvent('click', {
    bubbles: true,
    cancelable: true,
    view: window
  });
  
  document.body.dispatchEvent(event);
}

/**
 * 关闭所有浮层
 * 这里只做“触发组件自行关闭”，不直接改 DOM 样式
 */
function closeAllIViewPoppers() {
  if (!hasOpenedPoppers()) return;
  
  blurActiveElement();
  triggerBodyMousedown();
  triggerBodyClick();
}

/**
 * 绑定 #app 滚动自动关闭浮层
 * @param {Object} options
 * @param {String} options.selector 滚动容器选择器
 * @param {Boolean} options.useRaf 是否启用 requestAnimationFrame 节流
 * @param {String[]} options.ignoreSelectors 需要忽略的浮层滚动节点
 */
export function bindAppScrollClosePoppers(options = {}) {
  const {
    selector = '#app',
    useRaf = true,
    ignoreSelectors = defaultIgnoreScrollSelectors
  } = options;
  const scrollIgnoreSelectors = Array.isArray(ignoreSelectors) ? ignoreSelectors : defaultIgnoreScrollSelectors;
  
  const el = getAppScrollContainer(selector);
  if (!el) {
    console.warn(aliasb03645cf6abd4d56a73b13687765a5d8.t('key1002146'), selector);
    return;
  }
  
  // 防止重复绑定
  if (appScrollEl && appScrollHandler) {
    unbindAppScrollClosePoppers();
  }
  
  appScrollEl = el;
  
  appScrollHandler = function (event) {
    if (shouldIgnoreScroll(event, scrollIgnoreSelectors)) {
      return;
    }

    if (!useRaf) {
      closeAllIViewPoppers();
      return;
    }
    
    if (appScrollRaf) {
      cancelAnimationFrame(appScrollRaf);
    }
    
    appScrollRaf = requestAnimationFrame(() => {
      closeAllIViewPoppers();
      appScrollRaf = null;
    });
  };
  
  appScrollEl.addEventListener('scroll', appScrollHandler, true);
}

/**
 * 解绑 #app 滚动监听
 */
export function unbindAppScrollClosePoppers() {
  if (appScrollRaf) {
    cancelAnimationFrame(appScrollRaf);
    appScrollRaf = null;
  }
  
  if (appScrollEl && appScrollHandler) {
    appScrollEl.removeEventListener('scroll', appScrollHandler, true);
  }
  
  appScrollEl = null;
  appScrollHandler = null;
}
